import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/lib/constants';
import { useNavigate } from 'react-router';
import _basedLogo from '@/assets/images/hypertracker/based_logo.png';
import _hlbadge from '@/assets/images/hypertracker/hl_badge.png';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectMe } from '@/state/hypertracker/hypertrackerSelector';
import { ALERT_PLANS, APP_FOR_PAYMENT, PAYMENT_PERIOD } from '@/state/payments/paymentsTypes';
import { UPGRADE_TYPE } from '@/pages/HypertrackerPurchase/types';
import useConsumerDataQuery from '@/pages/HypertrackerApiDashboard/useConsumerDataQuery';

interface AccountTypeProps {
    closeMenu?: () => void;
    isOpen?: boolean;
}

const AccountType: React.FC<AccountTypeProps> = ({ closeMenu, isOpen = true }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'hypertracker_sidebar' });
    const navigate = useNavigate();
    const theme = useTheme();
    const me = useSelector((state: RootState) => selectMe(state));
    const { activeSubscriptions } = useConsumerDataQuery();
    const activeAlertSubscriptionDuration = activeSubscriptions?.find(
        (sub) => sub.applicationCode === APP_FOR_PAYMENT.HYPERTRACKER,
    )?.subscriptionPlanDuration as PAYMENT_PERIOD | undefined;

    const getPlanBadge = (planValue: ALERT_PLANS) => {
        switch (planValue) {
            case ALERT_PLANS.SCOUT:
                return {
                    label: t('plan_scout_label'),
                    labelColor: theme.palette.hypertracker.light,
                    background: theme.palette.hypertracker.light,
                    backgroundInside: theme.palette.backgrounds.layoutHeaderBg,
                };
            case ALERT_PLANS.PRO_TRACKER:
                return {
                    label: isOpen ? t('plan_pro_label') : 'PRO',
                    labelColor: theme.palette.hypertracker.light,
                    background: theme.palette.hypertracker.light,
                    backgroundInside: theme.palette.backgrounds.layoutHeaderBg,
                };
            case ALERT_PLANS.BASED_UNLIMITED:
                return {
                    label: isOpen ? t('plan_based_label') : '',
                    labelColor: theme.palette.based.main,
                    background: theme.palette.based.main,
                    backgroundInside: theme.palette.backgrounds.layoutHeaderBg,
                };
            case ALERT_PLANS.HYPERLIQUID:
                return {
                    label: isOpen ? t('plan_based_label') : '',
                    labelColor: theme.palette.hypertracker.light,
                    background: theme.palette.hypertracker.light,
                    backgroundInside: theme.palette.primitives.riptide[1000],
                };
            default:
                return {
                    label: isOpen ? t('plan_basic_label') : 'B',
                    labelColor: theme.palette.text.primary,
                    background: theme.palette.customText.text7,
                    backgroundInside: theme.palette.backgrounds.layoutHeaderBg,
                };
        }
    };

    const handleUpgradeNow = () => {
        if (me?.accountType === ALERT_PLANS.SCOUT) {
            const params = new URLSearchParams();
            params.append('plan', ALERT_PLANS.PRO_TRACKER);
            params.append('duration', activeAlertSubscriptionDuration ?? PAYMENT_PERIOD.YEAR);
            params.append('app', 'hypertracker');
            params.append('upgrade', UPGRADE_TYPE.TO_ENTERPRISE);
            navigate(`${ROUTES.HYPERTRACKER}/${ROUTES.HYPERTRACKER_PURCHASE}?${params.toString()}`);
        } else {
            navigate(ROUTES.HYPERTRACKER_PRICING);
        }
        if (closeMenu) closeMenu();
    };

    return (
        <Box
            display={'flex'}
            justifyContent={'space-between'}
            gap={'12px'}
            mx={isOpen ? '' : 'auto'}
            px={isOpen ? '16px' : 0}
            pb={closeMenu ? '12px' : '4px'}
            sx={{ backgroundColor: theme.palette.primitives.woodsmoke[1000] }}
        >
            <Stack
                alignSelf={'center'}
                justifyContent={'center'}
                p={'1px'}
                sx={{
                    backgroundColor: isOpen
                        ? getPlanBadge(me?.accountType || ALERT_PLANS.BASIC).background
                        : 'backgrounds.bg4',
                    clipPath:
                        'polygon(4px 0%, 100% 0%, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0% 100%, 0% 4px)',
                }}
            >
                <Stack
                    width={isOpen ? 'unset' : '42px'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    sx={{
                        backgroundColor: isOpen
                            ? getPlanBadge(me?.accountType || ALERT_PLANS.BASIC).backgroundInside
                            : 'backgrounds.layoutHeaderBg',
                        clipPath:
                            'polygon(4px 0%, 100% 0%, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0% 100%, 0% 4px)',
                    }}
                >
                    <Typography
                        variant={'p12'}
                        color={
                            isOpen
                                ? getPlanBadge(me?.accountType || ALERT_PLANS.BASIC).labelColor
                                : 'backgrounds.bg4'
                        }
                        px={isOpen ? '8px' : '4px'}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'default',
                            '&:hover': {
                                backgroundColor: alpha(
                                    getPlanBadge(me?.accountType || ALERT_PLANS.BASIC)
                                        ?.background || '',
                                    0.2,
                                ),
                            },
                        }}
                    >
                        {me?.accountType === ALERT_PLANS.BASED_UNLIMITED && (
                            <Box
                                sx={{ flexShrink: 0 }}
                                display={'flex'}
                                mt={isOpen ? 'auto' : '5px'}
                                mb={isOpen ? '1.5px' : '4px'}
                            >
                                <img src={_basedLogo} width={isOpen ? 50 : 32} alt={'based-logo'} />
                            </Box>
                        )}
                        {me?.accountType === ALERT_PLANS.HYPERLIQUID && (
                            <Box
                                sx={{ flexShrink: 0 }}
                                display={'flex'}
                                mt={isOpen ? 'auto' : '5px'}
                                mb={isOpen ? 'auto' : '4px'}
                            >
                                <img
                                    src={_hlbadge}
                                    width={isOpen ? 65 : 32}
                                    height={'auto'}
                                    alt={'hyperliquid-logo'}
                                />
                            </Box>
                        )}
                        {getPlanBadge(me?.accountType || ALERT_PLANS.BASIC).label}
                    </Typography>
                </Stack>
            </Stack>

            {(me?.accountType === ALERT_PLANS.BASIC || me?.accountType === ALERT_PLANS.SCOUT) &&
                isOpen && (
                    <Typography
                        variant={'p12'}
                        onClick={handleUpgradeNow}
                        color={'warning.main'}
                        sx={{
                            cursor: 'pointer',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {t('upgrade_plan')}
                    </Typography>
                )}
        </Box>
    );
};

export default AccountType;
